import { Button } from '@base-ui/react/button';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useState } from 'react';
import styled from 'styled-components';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import { Badge, type BadgeVariant } from '~/components/ui/Badge';
import { PageTitle } from '~/components/ui/Page';
import { Table } from '~/components/ui/Table';
import { useStudentFocus } from '~/modules/studentFocus';
import buttonStyles from '~/styles/button.module.css';

type GradingSubmissionSummary =
  components['schemas']['GradingSubmissionSummary'];

type FeedbackSearch = {
  quiz_response_id?: number;
};

export const Route = createFileRoute('/_admin/feedbacks/')({
  component: FeedbacksList,
  validateSearch: (search: Record<string, unknown>): FeedbackSearch => {
    const raw = search.quiz_response_id;
    const id =
      typeof raw === 'number'
        ? raw
        : typeof raw === 'string' && raw !== ''
          ? Number(raw)
          : undefined;
    return {
      quiz_response_id:
        id !== undefined && Number.isFinite(id) ? id : undefined,
    };
  },
});

const columnHelper = createColumnHelper<GradingSubmissionSummary>();

function statusBadge(status: string): {
  label: string;
  variant: BadgeVariant;
} {
  switch (status) {
    case 'GRADED':
      return { label: 'Graded', variant: 'success' };
    case 'PENDING':
      return { label: 'Pending', variant: 'warning' };
    case 'FAILED':
      return { label: 'Failed', variant: 'error' };
    default:
      return { label: status, variant: 'neutral' };
  }
}

const columns = [
  columnHelper.accessor('id', { header: 'ID' }),
  columnHelper.accessor((row) => row.student.display_name, {
    id: 'student',
    header: 'Student',
  }),
  columnHelper.accessor((row) => row.course.title, {
    id: 'course',
    header: 'Course',
  }),
  columnHelper.accessor((row) => row.lesson.title, {
    id: 'lesson',
    header: 'Lesson',
  }),
  columnHelper.accessor('score', {
    header: 'Score',
    cell: (info) => `${info.getValue()}%`,
  }),
  columnHelper.accessor('attempt_number', { header: 'Attempt' }),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: (info) => {
      const { label, variant } = statusBadge(info.getValue());
      return <Badge $variant={variant}>{label}</Badge>;
    },
  }),
  columnHelper.accessor('graded_at', {
    header: 'Graded',
    cell: (info) => {
      const v = info.getValue();
      return v ? new Date(v).toLocaleString() : '—';
    },
  }),
];

function FeedbacksList() {
  const search = Route.useSearch();
  const [page, setPage] = useState(0);
  const { focusedStudent } = useStudentFocus();

  const { data, isLoading, error } = api.adminListFeedbackSubmissions.useQuery({
    query: {
      limit: 10,
      offset: 10 * page,
      ...(search.quiz_response_id !== undefined
        ? { quiz_response_id: search.quiz_response_id }
        : {}),
      ...(focusedStudent !== null ? { student_id: focusedStudent.id } : {}),
    },
  });

  const table = useReactTable({
    data: data?.data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div>
      <PageTitle>Feedbacks</PageTitle>

      {search.quiz_response_id !== undefined ? (
        <FilterBanner>
          <span>
            Filtered to feedback for quiz submission{' '}
            <Link
              to="/quiz-submissions/$submissionId"
              params={{ submissionId: String(search.quiz_response_id) }}
            >
              #{search.quiz_response_id}
            </Link>
          </span>
          <Link to="/feedbacks" search={{}}>
            Clear filter
          </Link>
        </FilterBanner>
      ) : null}

      {isLoading ? (
        <LoadingMessage>Loading Feedbacks...</LoadingMessage>
      ) : error ? (
        <ErrorMessage>Error loading Feedbacks</ErrorMessage>
      ) : (
        <>
          <Toolbar>
            <div style={{ flexGrow: 1 }}>Page {page + 1}</div>
            <Button
              disabled={page === 0}
              className={buttonStyles.Button}
              onClick={() => setPage((v) => v - 1)}
            >
              Previous
            </Button>
            <Button
              className={buttonStyles.Button}
              onClick={() => setPage((v) => v + 1)}
            >
              Next
            </Button>
          </Toolbar>

          <Table
            table={table}
            to="/feedbacks/$submissionId"
            getParams={(row) => ({ submissionId: String(row.id) })}
          />
        </>
      )}
    </div>
  );
}

const Toolbar = styled.div`
  display: flex;
  flex-direction: row;
  margin: var(--component-margin);
  gap: var(--component-margin);
`;

const FilterBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.625rem 0.875rem;
  margin: var(--component-margin);
  background: color-mix(in srgb, var(--blue) 8%, transparent);
  border-left: 3px solid var(--blue);
  border-radius: 4px;

  a {
    color: var(--blue);
  }
`;

const LoadingMessage = styled.div`
  padding: 2rem;
  text-align: center;
  color: var(--grey-600);
`;

const ErrorMessage = styled.div`
  padding: 2rem;
  text-align: center;
  color: var(--error);
`;
